import { useContext } from 'react';
import RpCookieContext from '../contexts/RpCookieContext';
import type { RpCookieContextValue } from '../contexts/RpCookieContext';
import { isClient } from '../utils';

/**
 * Hook to access the Rp cookie value from the RpCookieContext.
 *
 * Must be used within a component that is a descendant of RpCookieProvider.
 * Throws an error if used outside of the provider.
 *
 * @returns Object containing the rpCookie string value
 * @throws {Error} If used outside of RpCookieProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { rpCookie } = useRpCookie();
 *
 *   return <div>Cookie: {rpCookie}</div>;
 * }
 * ```
 */
const useRpCookie = (): RpCookieContextValue => {
  const context = useContext(RpCookieContext);
  if (!isClient()) {
    return { rpCookie: '' };
  }
  if (context === undefined) {
    throw new Error('useRpCookie must be used within a RpCookieProvider');
  }
  return context;
};

export default useRpCookie;
